import React, { createContext, useContext, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { refreshCart: refreshCartCount, shopLoggedIn } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [loading, setLoading] = useState(false);

  const loadCart = useCallback(async () => {
    if (!shopLoggedIn) return;
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      console.error("Failed to load cart:", e); // Cart page shows its own empty/error state
    } finally {
      setLoading(false);
    }
  }, [shopLoggedIn]);

  const getCartQty = useCallback((productId) => {
    const item = cart.items.find((i) => i.product_id === productId);
    return item ? item.quantity : 0;
  }, [cart.items]);

  /**
   * Inventory enforcement: quantity may never exceed `product.stock` when the product
   * carries live stock (stock > 0). Items with stock === 0 are "Make to Order" (production
   * request) and have NO cap.
   */
  const validateQty = useCallback((product, requestedQty) => {
    const stock = Number(product?.stock ?? 0);
    if (stock > 0 && requestedQty > stock) {
      return { ok: false, message: `Only ${stock} piece${stock === 1 ? "" : "s"} available` };
    }
    return { ok: true };
  }, []);

  const isAtStockLimit = useCallback((product) => {
    const stock = Number(product?.stock ?? 0);
    if (stock <= 0) return false; // Make-to-Order items have no cap
    return getCartQty(product.id) >= stock;
  }, [getCartQty]);

  const addToCart = useCallback(async (product, qty = 1) => {
    if (!shopLoggedIn) {
      toast.error("Please log in");
      return false;
    }
    const existingQty = getCartQty(product.id);
    const check = validateQty(product, existingQty + qty);
    if (!check.ok) {
      toast.error(check.message);
      return false;
    }
    try {
      const { data } = await api.post("/cart", { product_id: product.id, quantity: qty });
      setCart(data);
      refreshCartCount();
      toast.success(Number(product.stock ?? 0) > 0 ? "Added to cart" : "Added — Make to Order");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to cart");
      return false;
    }
  }, [shopLoggedIn, getCartQty, validateQty, refreshCartCount]);

  const updateQty = useCallback(async (productId, qty, product) => {
    if (product) {
      const check = validateQty(product, qty);
      if (!check.ok) {
        toast.error(check.message);
        return false;
      }
    }
    try {
      const { data } = await api.put(`/cart/${productId}`, { product_id: productId, quantity: qty });
      setCart(data);
      refreshCartCount();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update cart");
      return false;
    }
  }, [validateQty, refreshCartCount]);

  const removeItem = useCallback(async (productId) => {
    try {
      const { data } = await api.delete(`/cart/${productId}`);
      setCart(data);
      refreshCartCount();
      return true;
    } catch (err) {
      toast.error("Could not remove item");
      return false;
    }
  }, [refreshCartCount]);

  return (
    <CartContext.Provider value={{
      cart, setCart, loading, loadCart,
      addToCart, updateQty, removeItem,
      getCartQty, validateQty, isAtStockLimit,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
